import { PageToolbar } from '@app/components/page-toolbar/PageToolbar';
import { PageToolbarLinearProgress } from '@app/components/page-toolbar/PageToolbarLinearProgress';
import { Box, Divider } from '@mui/material';
import { Formik } from 'formik';
import { ComponentType, PropsWithChildren } from 'react';
import { FieldValues, UseFormProps } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { useReportCtx } from '../report-ctx';
import { ReportActions } from './actions/formik/ReportActions';
import { ReportActionsRhf } from './actions/rhf/ReportActionsRhf';

export type FormLibrary = 'formik' | 'react-hook-form';

/**
 * Report form wrapped in PageToolbar with DataToolbar-style layout:
 * form content, divider, then actions bar.
 */
export const ReportForm = ({
  children,
  Actions = ReportActions,
  formLibrary = 'formik',
  rhfFormProps,
}: PropsWithChildren<{
  Actions?: ComponentType;
  horizontal?: boolean;
  formLibrary?: FormLibrary;
  /** Extra `useForm` options for React Hook Form reports (e.g. `resolver`, `mode`). `values` is always driven by report context. */
  rhfFormProps?: Partial<Omit<UseFormProps<FieldValues>, 'values'>>;
}>) => {
  const ctx = useReportCtx();

  if (formLibrary === 'react-hook-form') {
    return (
      <FormContainer
        {...rhfFormProps}
        values={ctx.values}
        onSuccess={(values) => ctx.runReport(values)}
        mode="onChange"
      >
        <PageToolbar>
          <Box>{children}</Box>
          <Divider sx={{ my: 1.25 }} />
          <ReportActionsRhf />
          <PageToolbarLinearProgress visible={ctx.isLoading} />
        </PageToolbar>
      </FormContainer>
    );
  }

  return (
    <Formik initialValues={ctx.values} onSubmit={(values) => ctx.runReport(values)}>
      <PageToolbar>
        <Box>{children}</Box>
        <Divider sx={{ my: 1.25 }} />
        <Actions />
        <PageToolbarLinearProgress visible={ctx.isLoading} />
      </PageToolbar>
    </Formik>
  );
};
